import { Component, OnInit, OnDestroy, NgZone, ViewChild } from '@angular/core';

import { Subscription } from 'rxjs';
import { ModalDirective } from 'ngx-bootstrap/modal';

import {
    Company,
    Minesite,
    User,
    Permission,
    CompanyService,
    MinesiteService,
    UserService,
    DataArray
} from './../../../shared/index';

@Component({
    selector: 'pmuser-list',
    templateUrl: './pmuser-list.html',
    standalone: false
})

export class PMUserListComponent implements OnInit, OnDestroy {

    @ViewChild('confirmPrint') confirmPrintModal: ModalDirective;
    @ViewChild('confirmNoPrint') confirmNoPrintModal: ModalDirective;
    @ViewChild('userFilter') userFilter: ModalDirective;

    private subscriptions: Subscription[] = [];

    public permissions: Permission[] = [];
    public companies: Company[] = [];
    public minesites: Minesite[] = [];
    public users: DataArray<User> = new DataArray<User>(1);

    public selectedPermissions: number[] = [];
    public selectedCompanies: number[] = [];
    public selectedMinesites: number[] = [];
    public selectedUsers: DataArray<User> = new DataArray<User>(1);

    public filteredUsers: User[] = [];

    public processing = true;
    public ascending = true;
    public currentPage = 1;
    public total = 0;
    public filterApplied = false;
    public allEmployees = true;
    public name = '';
    public ableToPrint = true;
    public notAbleToPrint = true;
    public reloadUsers = false;
    public previousSelectedUserId = 0;
    public currentSelectedUserId = 0;
    public userIndexMap = [] as any;

    constructor(
        private _ngZone: NgZone,
        private _companyService: CompanyService,
        private _minesiteService: MinesiteService,
        private _userService: UserService
    ) {
        this.subscriptions.push(this._minesiteService.minesitesChange.subscribe((info: any) => {
            this._ngZone.run(() => {
                this.minesites = info;
            });
        }));
        this.subscriptions.push(this._companyService.companysChange.subscribe((info: any) => {
            this._ngZone.run(() => {
                this.companies = info.companies;
                //TODO: add handling: also receives info.isFinalPage
            });
        }));

        this.subscriptions.push(this._userService.permissionChange.subscribe((info: any) => {
            this._ngZone.run(() => {
                this.permissions = info;
            });
        }));

        this.subscriptions.push(this._userService.usersChange.subscribe((users: User[]) => {
            this._ngZone.run(() => {
                this.total = this._userService.totalUsers;

                this.users.init();
                this.filteredUsers = [];

                let index = 1;
                for (const user of users) {
                    this.users.update(user, true);
                    // this dictionary array will store the order of User(Index) and corresponding unique ID of the User
                    this.userIndexMap.push({ 'Index': index, 'Id': user.Id });
                    index++;
                }

                // if reloading the users when printing preference have changed, reapply the filter
                if (this.reloadUsers) {

                    this.reloadUsers = false;

                    // reset the filter if applied
                    if (this.name != '' || this.allEmployees == false || this.selectedCompanies.length > 0 || this.selectedMinesites.length > 0 || this.selectedPermissions.length > 0 || (this.ableToPrint == true && this.notAbleToPrint == false) || (this.ableToPrint == false && this.notAbleToPrint == true)) {
                        this._userService.applyFilter(this.name, this.allEmployees, this.selectedCompanies, this.selectedMinesites, this.selectedPermissions, this.ableToPrint, this.notAbleToPrint);
                    }
                }

                this.filteredUsers = this.users.Data;
                this.processing = false;
            });
        }));

        this.subscriptions.push(this._userService.userSelectedChange.subscribe((user: User) => {
            if (user.Selected) {
                this.selectedUsers.update(user, true);
            } else {
                this.selectedUsers.delete(user);
            }
        }));

        this.subscriptions.push(this._userService.clearSelected.subscribe((result: boolean) => {
            for (const user of this.users.Data.filter((usr) => {
                return usr.Selected;
            })) {
                user.Selected = false;
            }
        }));

        this.subscriptions.push(this._userService.printChange.subscribe((result: boolean) => {
            this.processing = true;
            this.getPage(1);
        }));

    }

    ngOnInit(): void {
        this._companyService.getCompanies();
        this._minesiteService.getMinesites();
        this._userService.getPermissions();
        this.selectedUsers.init();
        this.users.init();

        this.getPage(1);
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    public getPage(page: number): void {
        this._userService.getUsers(page);
    }

    public selectUser(user: User, userIndex: number, event: MouseEvent): void {
        // set prev and curr selected userIndex for multi select between shift logic
        if (!user.Selected) { // set prev and current only if the User is not selected already.
            if (this.previousSelectedUserId == 0) {
                this.previousSelectedUserId = userIndex; // set prev from userIndex the very first time. No need to set curr for the very first time
            } else {
                if (this.currentSelectedUserId != 0) { // don't set prev to curr when curr is not set yet (first time)
                    this.previousSelectedUserId = this.currentSelectedUserId;
                }
                this.currentSelectedUserId = userIndex;
            }
        }

        if (!event.shiftKey) {
            user.Selected = !user.Selected;
            this._userService.selectUser(user);
        } else if (this.currentSelectedUserId != 0 && this.previousSelectedUserId != 0 && this.currentSelectedUserId > this.previousSelectedUserId) {
            this.getShiftSelectionUsers(this.previousSelectedUserId, this.currentSelectedUserId).forEach(shiftUser => {
                // select only the Users that are not selected already
                if (this.selectedUsers.Data.filter(sd => sd.Id == shiftUser.Id).length == 0) {
                    shiftUser.Selected = true;
                    this._userService.selectUser(shiftUser);
                }
            });
        }
    }

    public clearSelectedUsers(): void {
        this.selectedUsers.init();
        this._userService.clearAll();
        this.previousSelectedUserId = 0;
        this.currentSelectedUserId = 0;
    }

    public promptPrintConfirm(): void {
        this.confirmPrintModal.show();
    }
    public promptNoPrintConfirm(): void {
        this.confirmNoPrintModal.show();
    }
    public cancelPrint(): void {
        this.confirmPrintModal.hide();
    }
    public cancelNoPrint(): void {
        this.confirmNoPrintModal.hide();
    }

    public allowPrint(): void {
        const inUserIds: number[] = [];
        const idx = 0;
        for (let i = 0; i < this.selectedUsers.Data.length; i++) {
            inUserIds.push(this.selectedUsers.Data[i].Id)
        }

        this.processing = true;
        this.reloadUsers = true;
        this._userService.allowUsersToPrint(inUserIds);
        this.clearSelectedUsers();
        this.confirmPrintModal.hide();
    }

    public restrictPrint(): void {
        const inUserIds: number[] = [];
        const idx = 0;
        for (let i = 0; i < this.selectedUsers.Data.length; i++) {
            inUserIds.push(this.selectedUsers.Data[i].Id)
        }

        this.processing = true;
        this.reloadUsers = true;
        this._userService.restrictUsersFromPrint(inUserIds);
        this.clearSelectedUsers();
        this.confirmNoPrintModal.hide();
    }

    public showFilters(): void {
        this.userFilter.show();
    }

    public applyFilter(): void {
        if (this.name != '' || this.allEmployees == false || this.selectedCompanies.length > 0 || this.selectedMinesites.length > 0 || this.selectedPermissions.length > 0 || (this.ableToPrint == true && this.notAbleToPrint == false) || (this.ableToPrint == false && this.notAbleToPrint == true)) {
            this.filterApplied = true;
        } else {
            this.filterApplied = false;
        }
        this.processing = true;
        this._userService.applyFilter(this.name, this.allEmployees, this.selectedCompanies, this.selectedMinesites, this.selectedPermissions, this.ableToPrint, this.notAbleToPrint);
        this.userFilter.hide();
    }

    public employeePermissionIncluded(): boolean {
        return this.selectedPermissions.length == 0 || this.selectedPermissions.filter(permId => permId == 2).length > 0;
    }

    public clearFilter(): void {
        this.name = '';
        this.allEmployees = true;
        this.selectedMinesites = [];
        this.selectedCompanies = [];
        this.selectedPermissions = [];
        this.filterApplied = false;
        this.ableToPrint = true;
        this.notAbleToPrint = true;
        this.processing = true;
        this._userService.applyFilter(this.name, this.allEmployees, this.selectedCompanies, this.selectedMinesites, this.selectedPermissions, this.ableToPrint, this.notAbleToPrint);
    }

    private getShiftSelectionUsers(previous: number, current: number): User[] {
        const userIndices: number[] = [];

        for (let i = previous + 1; i <= current; i++) { // previous + 1 to skip the previous which is selected already
            userIndices.push(i);
        }

        const userIndexMaps = this.userIndexMap.filter((uim: { Index: number; }) => userIndices.indexOf(uim.Index) >= 0);
        const userIDs = [...Array.from(new Set(userIndexMaps.map((uim: { Id: any; }) => uim.Id)))];

        return this.users.Data.filter(user => userIDs.indexOf(user.Id) >= 0);

    }

}
